import React, { useEffect, useState } from 'react';
import { api, type ImportedMarksStatus } from '../api';
import { getTheme, useThemeMode } from '../theme';

/**
 * IM1: admin slår av eller på visningen av alla importerade marker (t.ex. från minjaktlagsplats.se).
 * Avslaget döljer dem och deras annonser i marknadsplatsen, sök, karta och på publika sidor; datan finns kvar.
 */
export function ImportedMarksSection() {
  const T = getTheme(useThemeMode().mode);
  const [status, setStatus] = useState<ImportedMarksStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { api.importedMarksStatus().then(setStatus).catch(e => setErr(e.message || String(e))); }, []);

  async function toggle() {
    if (!status) return;
    setSaving(true); setErr('');
    try { setStatus(await api.setShowImportedMarks(!status.showImported)); }
    catch (e: any) { setErr(e.message || String(e)); }
    finally { setSaving(false); }
  }

  async function toggleMinJakt() {
    if (!status) return;
    setSaving(true); setErr('');
    try { setStatus(await api.setHideImportedInMinJakt(!status.hideInMinJakt)); }
    catch (e: any) { setErr(e.message || String(e)); }
    finally { setSaving(false); }
  }

  const total = status?.sources.reduce((n, s) => n + s.marks, 0) ?? 0;
  const on = !!status?.showImported;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, padding: '16px 18px', marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.amber, letterSpacing: 1, marginBottom: 12, textTransform: 'uppercase' }}>Importerade marker</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: status ? 'pointer' : 'default' }}>
        <input type="checkbox" role="switch" aria-checked={on} checked={on} disabled={!status || saving} onChange={toggle} style={{ width: 20, height: 20, accentColor: T.amber }} />
        <span style={{ fontSize: 14, fontWeight: 600 }}>Visa importerade marker och annonser publikt</span>
      </label>
      <div style={{ fontSize: 12, color: T.textSub, marginTop: 8, lineHeight: 1.5 }}>
        {status
          ? <>
              {status.sources.map(s => `${s.label}: ${s.marks} marker (${s.published} publicerade)`).join(' · ')}
              <br />
              {on ? 'Visas i marknadsplatsen, sök, kartan och på markernas sidor.' : `Dolda för alla${total ? ` (${total} marker)` : ''}. Datan finns kvar och kan visas igen.`}
              {status.updatedBy && <> Senast ändrad av {status.updatedBy}.</>}
            </>
          : !err && 'Läser…'}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: status ? 'pointer' : 'default', marginTop: 16 }}>
        <input type="checkbox" role="switch" aria-checked={!!status?.hideInMinJakt} checked={!!status?.hideInMinJakt} disabled={!status || saving} onChange={toggleMinJakt} style={{ width: 20, height: 20, accentColor: T.amber }} />
        <span style={{ fontSize: 14, fontWeight: 600 }}>Dölj importerade marker i Min jakt</span>
      </label>
      <div style={{ fontSize: 12, color: T.textSub, marginTop: 8, lineHeight: 1.5 }}>
        Gäller bara dig. Importerade marker där du är riktig medlem eller ägare visas ändå, och reglaget ovan påverkas inte.
      </div>
      {err && <div style={{ fontSize: 12, color: '#f87171', marginTop: 8 }}>{err}</div>}
    </div>
  );
}
